import React from "react";
import { motion } from "framer-motion";
import { Shield, Leaf, Award, Heart } from "lucide-react";

const features = [
  {
    icon: Shield,
    title: "حلول تسويقية متكاملة",
    description: "نقدّم استراتيجيات رقمية وإعلانات مبتكرة تُحوّل أفكارك إلى حضور مؤثر."
  },
  {
    icon: Leaf,
    title: "إبداع وهوية بصرية",
    description: "تصميم شعارات وهويات للعلامات التجارية تعكس شخصيتها وقيمها."
  },
  {
    icon: Award,
    title: "فريق محترف ومؤهل",
    description: "خبراء في التسويق الرقمي، التصميم، الإنتاج والطباعة لضمان أعلى جودة."
  },
  {
    icon: Heart,
    title: "تجربة مخصصة لكل عميل",
    description: "نقدم حلولًا مصممة خصيصًا لتلبية احتياجات علامتك التجارية وتحقيق أهدافك."
  }
];

const LeafDecoration = () => (
  <motion.svg 
    initial={{ opacity: 0, rotate: -90, scale: 0.5 }}
    whileInView={{ opacity: 0.6, rotate: 0, scale: 1 }}
    transition={{ duration: 2, ease: "easeOut" }}
    viewport={{ once: true }}
    width="120" height="80" viewBox="0 0 120 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-[#141d3c]"
  >
    <path d="M10 40C15 20 35 10 50 15C55 17 60 22 65 30" stroke="currentColor" strokeWidth="1.5" fill="none" opacity="0.6"/>
    <path d="M50 15C52 12 58 8 65 12C70 15 75 22 80 35" stroke="currentColor" strokeWidth="1.5" fill="none" opacity="0.6"/>
    <path d="M65 30C68 25 75 20 85 25C90 28 95 35 100 50" stroke="currentColor" strokeWidth="1.5" fill="none" opacity="0.6"/>
    <path d="M20 50C25 45 35 40 45 45C50 48 55 55 60 70" stroke="currentColor" strokeWidth="1.5" fill="none" opacity="0.6"/>
    <path d="M45 45C48 42 55 38 65 42C70 45 75 52 80 65" stroke="currentColor" strokeWidth="1.5" fill="none" opacity="0.6"/>
    <ellipse cx="25" cy="35" rx="8" ry="12" fill="currentColor" opacity="0.3" transform="rotate(-30 25 35)"/>
    <ellipse cx="55" cy="25" rx="6" ry="10" fill="currentColor" opacity="0.3" transform="rotate(-45 55 25)"/>
    <ellipse cx="75" cy="45" rx="7" ry="11" fill="currentColor" opacity="0.3" transform="rotate(-60 75 45)"/>
    <ellipse cx="35" cy="55" rx="5" ry="9" fill="currentColor" opacity="0.3" transform="rotate(-20 35 55)"/>
  </motion.svg>
);

export default function WhatWeDo() {
  return (
    
    <section className="font-cairo py-16 md:py-20 lg:py-24 bg-white relative overflow-hidden">
      {/* Enhanced Background Decorations */}
      <motion.div 
        initial={{ opacity: 0, x: -200, rotate: -45 }}
        whileInView={{ opacity: 0.2, x: 0, rotate: 0 }}
        transition={{ duration: 2, ease: "easeOut" }}
        viewport={{ once: true }}
        className="absolute top-10 left-10"
      >
        <LeafDecoration />
      </motion.div>
      <motion.div 
        initial={{ opacity: 0, x: 200, rotate: 45 }}
        whileInView={{ opacity: 0.2, x: 0, rotate: 180 }}
        transition={{ duration: 2, ease: "easeOut", delay: 0.5 }}
        viewport={{ once: true }}
        className="absolute bottom-10 right-10 transform"
      >
        <LeafDecoration />
      </motion.div>

      <div className="font-cairo max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 80, scale: 0.8 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 1.2, ease: "easeOut", type: "spring", stiffness: 100 }}
          viewport={{ once: true }}
          className="font-cairo text-center mb-16"
        >
          <motion.p 
            initial={{ opacity: 0, letterSpacing: "0.1em" }}
            whileInView={{ opacity: 1, letterSpacing: "0.3em" }}
            transition={{ duration: 1, delay: 0.3 }}
            viewport={{ once: true }}
            className="font-cairo text-sm text-gray-500 mb-4 tracking-wider uppercase"
          >
            ZAN DIGITAL MARKETING
          </motion.p>
          <motion.h2 
            initial={{ opacity: 0, y: 50, skewX: 10 }}
            whileInView={{ opacity: 1, y: 0, skewX: 0 }}
            transition={{ duration: 1, delay: 0.5, ease: "easeOut" }}
            viewport={{ once: true }}
            className="font-cairo text-4xl md:text-5xl lg:text-6xl font-bold text-[#141D3C] leading-tight mb-6"
          >
            زان التسويـقية الرقميـة
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 30, filter: 'blur(5px)' }}
            whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            transition={{ duration: 1, delay: 1, ease: "easeOut" }}
            viewport={{ once: true }}
            className="font-cairo text-lg text-gray-600 max-w-4xl mx-auto leading-relaxed"
          >
            زان التسويقية الرقمية شركة سعودية متكاملة، تقدّم حلولًا رقمية وإبداعية شاملة تشمل 
            الاستشارات التسويقية، التصميم، الإنتاج الإعلامي والطباعة، لضمان تميز 
            حضور علامتك التجارية وتحقيق أهدافك بكفاءة واحترافية.
          </motion.p>
        </motion.div>

        {/* Main Grid Layout - Image and Features */}
        <div className="font-cairo grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          
          {/* Right Side - Single Featured Image */}
          <motion.div
            initial={{ opacity: 0, x: 100, scale: 0.85 }}
            whileInView={{ opacity: 1, x: 0, scale: 1 }}
            transition={{ duration: 1.4, ease: "easeOut", type: "spring", stiffness: 80 }}
            viewport={{ once: true }}
            className="relative order-1 lg:order-2"
          >
            {/* Decorative Background Elements */}
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, delay: 0.3 }}
              viewport={{ once: true }}
              className="absolute -top-6 -right-6 w-32 h-32 bg-gradient-to-br from-[#141d3c]/10 to-[#ee9805]/10 rounded-full blur-2xl"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, delay: 0.5 }}
              viewport={{ once: true }}
              className="absolute -bottom-8 -left-8 w-40 h-40 bg-gradient-to-tr from-[#ee9805]/15 to-[#141d3c]/10 rounded-full blur-3xl"
            />
            
            {/* Main Image Container */}
            <motion.div
              whileHover={{ 
                scale: 1.02,
                boxShadow: "0 30px 60px rgba(20, 29, 60, 0.25)"
              }}
              transition={{ duration: 0.5 }}
              className="relative rounded-3xl overflow-hidden shadow-2xl group"
            >
              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#141d3c]/40 via-transparent to-transparent z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              
              {/* Image */}
              <motion.img
                src="https://imgg.io/images/2025/12/17/25eb9655c01ac32df92941a3573a3b9f.jpg"
                alt="زان التسويقية الرقمية"
                className="w-full h-[400px] md:h-[500px] lg:h-[550px] object-cover transition-transform duration-700 group-hover:scale-105"
              />
              
              {/* Floating Badge */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.8 }}
                viewport={{ once: true }}
                className="absolute bottom-6 right-6 bg-white/95 backdrop-blur-sm px-6 py-3 rounded-2xl shadow-xl z-20"
              >
                <p className="text-[#141d3c] font-bold text-lg font-cairo">نصنع النجاح معًا</p>
                <p className="text-gray-500 text-sm font-cairo">شريكك في التميز الرقمي</p>
              </motion.div>
              
              {/* Corner Accent */}
              <div className="absolute top-0 left-0 w-24 h-24 bg-gradient-to-br from-[#ee9805] to-[#ee9805]/50 opacity-80 rounded-br-[60px]" />
            </motion.div>
            
            {/* Decorative Dots Pattern */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 0.4 }}
              transition={{ duration: 1, delay: 0.6 }}
              viewport={{ once: true }}
              className="absolute -bottom-4 right-8 grid grid-cols-4 gap-2"
            >
              {[...Array(12)].map((_, i) => (
                <div key={i} className="w-2 h-2 bg-[#141d3c] rounded-full" />
              ))}
            </motion.div>
          </motion.div>

          {/* Left Side - Features */}
          <div className="order-2 lg:order-1">
            <div className="grid grid-cols-1 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ 
                    opacity: 0, 
                    x: -80, 
                    scale: 0.9
                  }}
                  whileInView={{ 
                    opacity: 1, 
                    x: 0, 
                    scale: 1
                  }}
                  transition={{ 
                    duration: 0.8, 
                    delay: index * 0.15,
                    type: "spring",
                    stiffness: 100,
                    damping: 15
                  }}
                  viewport={{ once: true }}
                  whileHover={{ 
                    scale: 1.03, 
                    x: 10,
                    boxShadow: "0 15px 30px rgba(0,0,0,0.08)"
                  }}
                  className="flex items-start font-cairo gap-5 group cursor-pointer p-5 rounded-2xl transition-all duration-300 hover:bg-gray-50/80"
                >
                  {/* Icon */}
                  <motion.div 
                    initial={{ scale: 0, rotate: -180 }}
                    whileInView={{ scale: 1, rotate: 0 }}
                    transition={{ 
                      duration: 0.6, 
                      delay: index * 0.15 + 0.2,
                      type: "spring",
                      stiffness: 200
                    }}
                    viewport={{ once: true }}
                    whileHover={{ 
                      rotate: 360, 
                      scale: 1.15,
                      backgroundColor: "#141d3c"
                    }}
                    className="w-14 h-14 bg-gradient-to-br from-[#e8e9eb] to-[#d5d6d8] rounded-2xl flex items-center font-cairo justify-center group-hover:from-[#141d3c] group-hover:to-[#1e2a4a] transition-all duration-500 shadow-md flex-shrink-0"
                  >
                    <feature.icon className="w-7 h-7 text-[#141d3c] group-hover:text-white font-cairo transition-colors duration-300" />
                  </motion.div>
                  
                  {/* Content */}
                  <div className="flex-1">
                    <motion.h3 
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.15 + 0.3 }}
                      viewport={{ once: true }}
                      className="font-cairo text-xl font-bold text-[#0F0F0F] mb-2 group-hover:text-[#141d3c] transition-colors duration-300"
                    >
                      {feature.title}
                    </motion.h3>
                    <motion.p 
                      initial={{ opacity: 0, x: -15 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.4, delay: index * 0.15 + 0.4 }}
                      viewport={{ once: true }}
                      className="font-cairo text-sm text-gray-600 leading-relaxed"
                    >
                      {feature.description}
                    </motion.p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.9 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 1, delay: 0.8, ease: "easeOut" }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <motion.button 
            whileHover={{ scale: 1.1, boxShadow: "0 20px 40px rgba(20, 29, 60, 0.3)" }}
            whileTap={{ scale: 0.95 }}
            onClick={() => window.dispatchEvent(new CustomEvent('open-booking-modal'))}
            className="bg-[#141d3c] text-white px-20 py-4 rounded-full font-cairo font-semibold border-4 border-transparent hover:bg-[#ee9805] hover:border-[#ee9805]/30 transition-all duration-300 shadow-lg text-lg"
          >
            ابـدأ مـع زان الآن
          </motion.button>
        </motion.div>
      </div>
      {/* Section Divider */}
<div className="absolute bottom-0 left-0 w-full">
  <div className="h-[4px] bg-gray-500"></div>
</div>

    </section>
  );
}